import React from "react";
import { startOfDay, endOfDay } from "date-fns";
import { ListFilter } from "lucide-react";
import {
  BillingPaymentMethod,
  BillingTransactionFilterType,
  BillingTransactionStatus,
} from "@/types/billing-transactions";
import { useTranslation } from "react-i18next";
import HotelsFilterSelectField from "./HotelsFilterSelect";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import BillingTransactionTypeField from "./BillingTransactionTypeField";
import BillingTransactionStatusField from "./BillingTransactionStatusField";
import BillingTransactionPaymentMethodField from "./BillingTransactionPaymentMethodField";
import { GuestFilterSelectField } from "./GuestFilterSelectField";
import BillingTransactionServiceField from "./BillingTransactionServiceField";
import { DateRangePicker } from "@/components/common/DateRangePicker";
import type { DateRange } from "@/hooks/useDateRange";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

type SortField = "amount" | "type" | "status" | "paymentMethod" | "createdAt";
type SortDirection = "asc" | "desc" | null;

const FILTER_CONTROL_CLASS =
  "w-full min-w-0 max-w-full justify-between gap-2 !min-h-8 !h-8 !px-4 !py-1 !border !border-slate-200 text-foreground !text-sm !font-medium shadow-xs overflow-hidden [&_[data-slot=select-value]]:block [&_[data-slot=select-value]]:min-w-0 [&_[data-slot=select-value]]:max-w-full [&_[data-slot=select-value]]:truncate";

type BillingTransactionsUiFilters = {
  status: BillingTransactionStatus | "all statuses";
  type: BillingTransactionFilterType | "all types";
  paymentMethod: BillingPaymentMethod | "all methods";
  serviceId?: string;
  guestId?: string;
  fromDate: string;
  toDate: string;
  hotelId?: string | "all hotels" | undefined;
  minAmount: string;
  maxAmount: string;
  currentPage: number;
  sortField: SortField | null;
  sortDirection: SortDirection;
  selectedTransactions: string[];
};

interface BillingTransactionsFilterProps {
  currentHotelId?: string;
  filters: BillingTransactionsUiFilters;
  actions: {
    setHotelId: (value: string | "all hotels" | undefined) => void;
    setStatus: (value: BillingTransactionStatus | "all statuses") => void;
    setType: (value: BillingTransactionFilterType | "all types") => void;
    setPaymentMethod: (value: BillingPaymentMethod | "all methods") => void;
    setServiceId: (value?: string) => void;
    setGuestId: (value?: string) => void;
    setFromDate: (value: string) => void;
    setToDate: (value: string) => void;
    setMinAmount: (value: string) => void;
    setMaxAmount: (value: string) => void;
    handleFilterChange: (setter: (value: any) => void, value: any) => void;
  };
}

const BillingTransactionsFilter = ({
  currentHotelId,
  filters,
  actions,
}: BillingTransactionsFilterProps) => {
  const { t } = useTranslation();
  const { userProfile, isLoading } = useAuthenticatedUser();
  let guestFilterHotelId = currentHotelId;
  let serviceFilterHotelId = currentHotelId;

  if (userProfile?.role === "TENANT_ADMIN") {
    guestFilterHotelId =
      filters.hotelId && filters.hotelId !== "all hotels"
        ? filters.hotelId
        : undefined;
    serviceFilterHotelId = guestFilterHotelId;
  }

  const toDateOrUndefined = (value: string) => {
    if (!value) return undefined;

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
  };

  const dateRange: DateRange = {
    from: toDateOrUndefined(filters.fromDate),
    to: toDateOrUndefined(filters.toDate),
  };

  const handleDateRangeChange = (range: DateRange) => {
    actions.handleFilterChange(
      actions.setFromDate,
      range.from ? startOfDay(range.from).toISOString() : "",
    );
    actions.handleFilterChange(
      actions.setToDate,
      range.to ? endOfDay(range.to).toISOString() : "",
    );
  };

  const handleMinAmountChange = (value: string) => {
    if (value === "") {
      actions.handleFilterChange(actions.setMinAmount, value);
      return;
    }

    const normalized = String(Math.max(0, Number(value)));
    actions.handleFilterChange(actions.setMinAmount, normalized);
  };

  const handleMaxAmountChange = (value: string) => {
    if (value === "") {
      actions.handleFilterChange(actions.setMaxAmount, value);
      return;
    }

    const normalized = String(Math.max(0, Number(value)));
    actions.handleFilterChange(actions.setMaxAmount, normalized);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="icon-text"
          className="gap-2 min-h-8 h-8 py-1 border border-slate-200 text-foreground text-sm font-medium shadow-xs"
        >
          <ListFilter className="size-4" />
          {t("common.filter")}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[380px] max-w-[calc(100vw-2rem)] p-3"
        align="start"
      >
        <div className="grid gap-2">
          <BillingTransactionStatusField
            withLabel={false}
            value={filters.status}
            triggerClassName={FILTER_CONTROL_CLASS}
            onChange={(value) =>
              actions.handleFilterChange(actions.setStatus, value)
            }
          />
          <BillingTransactionTypeField
            withLabel={false}
            value={filters.type}
            triggerClassName={FILTER_CONTROL_CLASS}
            onChange={(value) =>
              actions.handleFilterChange(actions.setType, value)
            }
          />
          <BillingTransactionPaymentMethodField
            withLabel={false}
            value={filters.paymentMethod}
            triggerClassName={FILTER_CONTROL_CLASS}
            onChange={(value) =>
              actions.handleFilterChange(actions.setPaymentMethod, value)
            }
          />
          {userProfile?.role === "TENANT_ADMIN" && !isLoading && (
            <HotelsFilterSelectField
              value={filters.hotelId}
              withLabel={false}
              triggerClassName={FILTER_CONTROL_CLASS}
              onChange={(value) => {
                actions.handleFilterChange(actions.setHotelId, value);
                actions.setServiceId(undefined);
                actions.setGuestId(undefined);
              }}
            />
          )}
          <BillingTransactionServiceField
            hotelId={serviceFilterHotelId}
            value={filters.serviceId}
            triggerClassName={FILTER_CONTROL_CLASS}
            onChange={(value) =>
              actions.handleFilterChange(
                actions.setServiceId,
                value === "all services" ? undefined : value,
              )
            }
          />
          <GuestFilterSelectField
            hotelId={guestFilterHotelId}
            value={filters.guestId}
            triggerClassName={FILTER_CONTROL_CLASS}
            onChange={(value) =>
              actions.handleFilterChange(actions.setGuestId, value)
            }
          />
          <DateRangePicker
            dateRange={dateRange}
            onDateRangeChange={handleDateRangeChange}
            className={FILTER_CONTROL_CLASS}
          />
          <div className="grid grid-cols-2 gap-2">
            <Input
              type="number"
              min="0"
              value={filters.minAmount}
              placeholder={t("billing.minAmount")}
              onChange={(e) => handleMinAmountChange(e.target.value)}
              className="!h-8 !min-h-8 !px-4 !py-1 border-slate-200 text-foreground !text-sm !font-medium shadow-xs"
            />
            <Input
              type="number"
              min="0"
              value={filters.maxAmount}
              placeholder={t("billing.maxAmount")}
              onChange={(e) => handleMaxAmountChange(e.target.value)}
              className="!h-8 !min-h-8 !px-4 !py-1 border-slate-200 text-foreground !text-sm !font-medium shadow-xs"
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default BillingTransactionsFilter;
